import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';

interface LogEvent {
  id: string;
  type: 'generation' | 'revision' | 'status_change' | 'pipeline_run';
  timestamp: string;
  title: string;
  detail: string;
  article_id: string | null;
  meta: Record<string, any>;
}

const TYPE_CONFIG: Record<string, { icon: string; color: string; bg: string; border: string; label: string }> = {
  generation:    { icon: 'auto_awesome',   color: '#4f46e5', bg: 'rgba(79,70,229,0.08)',  border: 'rgba(79,70,229,0.2)',  label: 'Generated' },
  revision:      { icon: 'edit',           color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)', label: 'Revised' },
  status_change: { icon: 'published_with_changes', color: '#10b981', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.2)', label: 'Published' },
  pipeline_run:  { icon: 'hub',            color: '#6366f1', bg: 'rgba(99,102,241,0.08)', border: 'rgba(99,102,241,0.2)', label: 'Pipeline' },
};

const FILTER_TYPES = [
  { key: 'all',          label: 'All Activity',  icon: 'list' },
  { key: 'pipeline_run', label: 'Pipeline Runs', icon: 'hub' },
  { key: 'generation',   label: 'Generated',     icon: 'auto_awesome' },
  { key: 'revision',     label: 'Revisions',     icon: 'edit' },
  { key: 'status_change',label: 'Published',     icon: 'published_with_changes' },
];

const LogsPage: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents]         = useState<LogEvent[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [filter, setFilter]         = useState('all');
  const [search, setSearch]         = useState('');
  const [expanded, setExpanded]     = useState<Set<string>>(new Set());
  const [page, setPage]             = useState(1);
  const PAGE_SIZE = 30;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getActivityLogs();
      setEvents(data.events || []);
    } catch {
      setError('Failed to load logs. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const fmt = (ts: string) => {
    if (!ts) return '—';
    try {
      const d = new Date(ts);
      return d.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      });
    } catch { return ts; }
  };

  const timeAgo = (ts: string) => {
    if (!ts) return '';
    try {
      const diff = Date.now() - new Date(ts).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1)  return 'just now';
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24)  return `${hrs}h ago`;
      return `${Math.floor(hrs / 24)}d ago`;
    } catch { return ''; }
  };

  const filtered = events.filter(e => {
    if (filter !== 'all' && e.type !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        e.title.toLowerCase().includes(q) ||
        e.detail.toLowerCase().includes(q) ||
        (e.meta?.source || '').toLowerCase().includes(q) ||
        (e.meta?.category || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Stats
  const counts = {
    pipeline_run:  events.filter(e => e.type === 'pipeline_run').length,
    generation:    events.filter(e => e.type === 'generation').length,
    revision:      events.filter(e => e.type === 'revision').length,
    status_change: events.filter(e => e.type === 'status_change').length,
  };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>Activity Logs</h1>
          <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>
            Full history of article generation, edits, revisions and pipeline runs
          </p>
        </div>
        <button
          onClick={load}
          title="Refresh logs"
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
            borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff',
            fontSize: 13, color: '#475569', cursor: 'pointer', fontWeight: 500,
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>refresh</span>
          Refresh
        </button>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { key: 'pipeline_run',  label: 'Pipeline Runs',  icon: 'hub' },
          { key: 'generation',    label: 'Articles Made',  icon: 'auto_awesome' },
          { key: 'revision',      label: 'Revisions',      icon: 'edit' },
          { key: 'status_change', label: 'Published',      icon: 'published_with_changes' },
        ].map(({ key, label, icon }) => {
          const cfg = TYPE_CONFIG[key];
          return (
            <div
              key={key}
              onClick={() => { setFilter(key); setPage(1); }}
              style={{
                background: filter === key ? cfg.bg : '#fff',
                border: `1px solid ${filter === key ? cfg.border : '#e2e8f0'}`,
                borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 10,
                transition: 'all 0.15s',
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 8, background: cfg.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18, color: cfg.color }}>{icon}</span>
              </div>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                  {counts[key as keyof typeof counts]}
                </div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter + search */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <span className="material-symbols-outlined" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 15, color: '#94a3b8' }}>search</span>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by headline, source, category…"
            style={{ width: '100%', padding: '8px 10px 8px 32px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', outline: 'none', background: '#fff' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {FILTER_TYPES.map(ft => (
            <button
              key={ft.key}
              onClick={() => { setFilter(ft.key); setPage(1); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600,
                background: filter === ft.key ? '#4f46e5' : '#f1f5f9',
                color: filter === ft.key ? '#fff' : '#475569',
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 13 }}>{ft.icon}</span>
              {ft.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 14 }}>
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
          <div style={{ width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#4f46e5', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          Loading activity logs…
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 44, display: 'block', marginBottom: 10 }}>history</span>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#475569', marginBottom: 4 }}>No activity yet</div>
          <div style={{ fontSize: 13 }}>
            {filter !== 'all' || search ? 'No entries match your filter.' : 'Run the pipeline to start generating articles.'}
          </div>
        </div>
      ) : (
        <>
          {/* Results count */}
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>
            Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} events
          </div>

          {/* Timeline */}
          <div style={{ position: 'relative' }}>
            {/* Vertical line */}
            <div style={{ position: 'absolute', left: 19, top: 0, bottom: 0, width: 2, background: '#e2e8f0', zIndex: 0 }} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {paginated.map(event => {
                const cfg    = TYPE_CONFIG[event.type] || TYPE_CONFIG.generation;
                const isOpen = expanded.has(event.id);
                const hasMeta = Object.keys(event.meta || {}).length > 0;
                const canNav = !!event.article_id;

                return (
                  <div key={event.id} style={{ position: 'relative', zIndex: 1 }}>
                    <div
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12,
                        background: '#fff', border: `1px solid ${isOpen ? cfg.border : '#e2e8f0'}`,
                        borderRadius: 10, padding: '10px 14px',
                        marginLeft: 0,
                        transition: 'border-color 0.15s',
                        cursor: hasMeta ? 'pointer' : 'default',
                      }}
                      onClick={() => hasMeta && toggleExpand(event.id)}
                    >
                      {/* Icon dot */}
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', background: cfg.bg,
                        border: `2px solid ${cfg.border}`, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', flexShrink: 0, position: 'relative', zIndex: 2,
                      }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 15, color: cfg.color }}>{cfg.icon}</span>
                      </div>

                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10,
                            background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                            textTransform: 'uppercase', letterSpacing: '0.05em',
                          }}>
                            {cfg.label}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{event.title}</span>
                          {event.meta?.category && (
                            <span style={{ fontSize: 11, color: '#94a3b8', background: '#f1f5f9', padding: '1px 7px', borderRadius: 10 }}>
                              {event.meta.category}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: '#475569', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 560 }}>
                          {event.detail}
                        </div>
                        {event.meta?.source && (
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 12, verticalAlign: 'middle' }}>rss_feed</span>
                            {' '}{event.meta.source}
                          </div>
                        )}
                      </div>

                      {/* Right side */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                        <span style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }} title={fmt(event.timestamp)}>
                          {timeAgo(event.timestamp)}
                        </span>
                        <span style={{ fontSize: 10, color: '#cbd5e1', whiteSpace: 'nowrap' }}>
                          {fmt(event.timestamp).split(',')[0]}
                        </span>
                        <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                          {canNav && (
                            <button
                              onClick={e => { e.stopPropagation(); navigate(`/article/${event.article_id}`); }}
                              title="Open article"
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#4f46e5', padding: 2, display: 'flex', alignItems: 'center' }}
                            >
                              <span className="material-symbols-outlined" style={{ fontSize: 15 }}>open_in_new</span>
                            </button>
                          )}
                          {hasMeta && (
                            <span className="material-symbols-outlined" style={{ fontSize: 15, color: '#cbd5e1', transition: 'transform 0.15s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                              expand_more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded detail panel */}
                    {isOpen && hasMeta && (
                      <div style={{
                        marginLeft: 44, marginTop: -1, marginBottom: 0,
                        background: '#f8fafc', border: `1px solid ${cfg.border}`,
                        borderTop: 'none', borderRadius: '0 0 10px 10px',
                        padding: '12px 16px',
                      }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                          {/* Timestamp */}
                          <MetaCell icon="schedule" label="Timestamp" value={fmt(event.timestamp)} />

                          {/* Per-type fields */}
                          {event.type === 'generation' && <>
                            {event.meta.source       && <MetaCell icon="rss_feed" label="News Source" value={event.meta.source} />}
                            {event.meta.category     && <MetaCell icon="label" label="Category" value={event.meta.category} />}
                            {event.meta.status       && <MetaCell icon="flag" label="Status" value={event.meta.status} />}
                            {event.meta.word_count   != null && <MetaCell icon="format_size" label="Word Count" value={`${event.meta.word_count} words`} />}
                            {event.meta.style_score  != null && <MetaCell icon="style" label="Style Score" value={`${event.meta.style_score}/100`} />}
                            {event.meta.originality_score != null && <MetaCell icon="verified_user" label="Originality" value={`${event.meta.originality_score}%`} />}
                            {event.meta.fact_score   != null && <MetaCell icon="fact_check" label="Fact Confidence" value={`${event.meta.fact_score}/100`} />}
                          </>}

                          {event.type === 'revision' && <>
                            {event.meta.source           && <MetaCell icon="rss_feed" label="Source" value={event.meta.source} />}
                            {event.meta.model            && <MetaCell icon="smart_toy" label="Model Used" value={event.meta.model} />}
                            {event.meta.previous_headline && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>
                                  Previous Headline
                                </div>
                                <div style={{ fontSize: 12, color: '#475569', fontStyle: 'italic' }}>
                                  "{event.meta.previous_headline}"
                                </div>
                              </div>
                            )}
                            {event.meta.feedback && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 4 }}>
                                  Revision Feedback
                                </div>
                                <div style={{ fontSize: 12, color: '#475569', background: 'rgba(245,158,11,0.06)', padding: '6px 10px', borderRadius: 6, borderLeft: '3px solid #f59e0b' }}>
                                  {event.meta.feedback}
                                </div>
                              </div>
                            )}
                          </>}

                          {event.type === 'pipeline_run' && <>
                            {event.meta.articles_generated != null && <MetaCell icon="article" label="Articles Generated" value={String(event.meta.articles_generated)} />}
                            {event.meta.sources_used       != null && <MetaCell icon="rss_feed" label="Sources Used" value={String(event.meta.sources_used)} />}
                            {event.meta.duration_seconds   != null && <MetaCell icon="timer" label="Duration" value={`${Math.round(event.meta.duration_seconds)}s`} />}
                            {event.meta.triggered_by       && <MetaCell icon="person" label="Triggered By" value={event.meta.triggered_by} />}
                          </>}

                          {event.type === 'status_change' && <>
                            {event.meta.status && <MetaCell icon="flag" label="New Status" value={event.meta.status} />}
                            {event.meta.source && <MetaCell icon="rss_feed" label="Source" value={event.meta.source} />}
                          </>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 20 }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}
              >
                ← Prev
              </button>
              {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    style={{
                      width: 32, height: 32, borderRadius: 8, border: '1px solid',
                      borderColor: page === p ? '#4f46e5' : '#e2e8f0',
                      background: page === p ? '#4f46e5' : '#fff',
                      color: page === p ? '#fff' : '#475569',
                      fontSize: 13, cursor: 'pointer', fontWeight: page === p ? 700 : 400,
                    }}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.4 : 1 }}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const MetaCell: React.FC<{ icon: string; label: string; value: string }> = ({ icon, label, value }) => (
  <div>
    <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
      <span className="material-symbols-outlined" style={{ fontSize: 12 }}>{icon}</span>
      {label}
    </div>
    <div style={{ fontSize: 12, color: '#1e293b', fontWeight: 500 }}>{value}</div>
  </div>
);

export default LogsPage;
